import React, { useState } from 'react'
import type { UxDraft, LogoSource } from '../utils/uxTemplateDraft'
import { showsDialog, promptTimeoutSeconds, normalizeAccent } from '../utils/uxTemplateDraft'

/**
 * What the end user will actually see, rendered from the template being edited
 * (016-template-model). Ported from the mock's `PsadtPreview` (sap-mockup/components/PsadtDialog)
 * at full fidelity: BOTH engines (Fluent and Classic), the real accent, the real logo/banner slot,
 * the light/dark Fluent themes, and the Welcome/Progress views.
 *
 * The dialog is drawn in PSADT's OWN colours, not our theme tokens, on purpose — it is a picture
 * of a Windows dialog, and re-skinning it to the portal's palette would preview a dialog no device
 * renders. Only the chrome around it (switchers, captions) follows the theme.
 *
 * The strings are the real ones from PSADT 4.1.8's strings.psd1 (Install and Uninstall variants),
 * because the point of a preview is the actual wording an admin's users will read. What is mocked
 * is the APP: a screen applying the template to a known app passes it as `sampleApp`; otherwise
 * a neutral example app (Contoso, Microsoft's sample company) stands in, since a template on its
 * own is not bound to an app.
 *
 * ASSETS. The defaults are the real files, not placeholders: the accent-band watermark is the
 * toolkit's own mark, from the design mock, the app-icon slot draws a generic app icon for the
 * example app, and `Banner.Classic.png` is the banner our packages ship, copied from aeam/templates/psadt/Assets.
 * A TEMPLATE PREVIEW DRAWS ONLY THESE. A template carries no image of its own: logo and banner
 * come from a branding package deployed to the device, which is authored and previewed on its
 * own screen (BrandingWizard, through PsadtDialogSurface). Reading the tenant's branding into
 * this preview would show an image the template does not ship, on a device that may not have
 * that branding — so nothing here reaches for it.
 *
 * The uninstall countdown shown in `notify` is capped by the dialog timeout, since that is the
 * value the deployment carries; every other countdown is shown as chosen.
 */

/** The toolkit's own mark, drawn white in the Fluent accent band. It is built-in chrome, not one
 *  of the three image slots, so it never follows a tenant's branding. */
const PSADT_GLYPH = '/psadt/psadt-logo.png'
// The Classic banner our packages ship (Assets\Banner.Classic.png), measured 450x100.
const DEFAULT_BANNER = '/psadt/Banner.Classic.png'
/** The app icon slot — `Assets.Logo`/`Assets.LogoDark`, which the dialog binds to AppIconImage and
 *  AppIconDarkImage. A template is not bound to an app, so this draws a generic icon for the example
 *  app the way a package ships the application's. PSADT takes one square asset for both themes. */
const APP_ICON = '/psadt/sample-app-icon.svg'
const APP_TITLE = 'Contoso App 1.0.0'
const PROCS: PreviewProcess[] = [{ name: 'Contoso App', exe: 'ContosoApp.exe' }]

/** One entry in the dialog's list of applications to close. */
export interface PreviewProcess {
  name: string
  /** The process name; omitted when only the application's name is known. */
  exe?: string
}

/**
 * The app a preview draws in the title, icon slot and close-apps list. A template on its own is
 * not bound to an app, so it previews with an example app; a screen that applies the template to a known
 * app passes that app so the preview shows what its users will read.
 */
export interface PreviewSampleApp {
  title: string
  processes?: PreviewProcess[]
  icon?: string
}

/** The app to draw, with the example app filling in when the caller supplies none. */
function sampleOf(cfg: PsadtPreviewConfig): {
  title: string
  icon: string | null
  procs: PreviewProcess[]
} {
  const app = cfg.sampleApp
  if (!app) return { title: APP_TITLE, icon: APP_ICON, procs: PROCS }
  return {
    title: app.title,
    // No icon known: a lettered tile, never another app's icon beside this app's name.
    icon: app.icon || null,
    // With no process list, the dialog lists the application itself.
    procs: app.processes?.length ? app.processes : [{ name: app.title }],
  }
}

// Verbatim from strings.psd1 (Install variants).
const S = {
  fluent: {
    dialogMessage:
      'Please save your work before continuing as the following applications will be closed automatically.',
    countdownLabel: 'Automatic Start Countdown',
    buttonLeft: 'Close Apps & Install',
    buttonRight: 'Defer',
    subtitleSuffix: ' - App Installation',
    progressMsg: 'Installation in progress. Please wait…',
    progressDetail: 'This window will close automatically when the installation is complete.',
  },
  classic: {
    closeAppsMsg:
      'The following programs must be closed before the installation can proceed.\n\nPlease save your work, close the programs, and then continue. Alternatively, save your work and click "Close Programs".',
    deferralsLabel: 'Remaining Deferrals:',
    countdownDefer: 'The installation will automatically continue in:',
    buttonClose: 'Close Programs',
    buttonDefer: 'Defer',
    buttonContinue: 'Continue',
    progressMsg: 'Installation in progress. Please wait…',
  },
}

// Uninstall variants — strings.psd1 keeps Install/Uninstall pairs; only the entries that differ
// are overridden.
const S_UNINSTALL = {
  fluent: {
    buttonLeft: 'Close Apps & Uninstall',
    subtitleSuffix: ' - App Uninstallation',
    progressMsg: 'Uninstallation in progress. Please wait…',
    progressDetail: 'This window will close automatically when the uninstallation is complete.',
  },
  classic: {
    closeAppsMsg:
      'The following programs must be closed before the uninstallation can proceed.\n\nPlease save your work, close the programs, and then continue. Alternatively, save your work and click "Close Programs".',
    countdownDefer: 'The uninstallation will automatically continue in:',
    progressMsg: 'Uninstallation in progress. Please wait…',
  },
}

function stringsFor(cfg: PsadtPreviewConfig) {
  if (cfg.deploymentType !== 'uninstall') return S
  return {
    fluent: { ...S.fluent, ...S_UNINSTALL.fluent },
    classic: { ...S.classic, ...S_UNINSTALL.classic },
  }
}

/** Classic renders hh:mm:ss. Exported and parity-tested — the format is part of what the user
 *  reads on the device. */
export function fmtCountdown(total: number): string {
  const t = Math.max(0, Math.floor(total || 0))
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  const s = t % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Fluent renders "0h 5m 00s" rather than a clock. */
export function fmtHMS(total: number): string {
  const t = Math.max(0, Math.floor(total || 0))
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  const s = t % 60
  return `${h}h ${m}m ${String(s).padStart(2, '0')}s`
}

export type PreviewPhase = 'install' | 'uninstall'
export type PsadtView = 'welcome' | 'progress'

export interface PsadtPreviewConfig {
  style: 'fluent' | 'classic'
  accent: string
  companyName: string
  logoSource: LogoSource
  /** Draw `logoUrl` in the Fluent logo slot instead of the app icon. */
  hasLogo: boolean
  /** Draw `bannerUrl` in the Classic banner slot instead of the shipped banner. */
  hasBanner: boolean
  /** Where to draw the two brandable slots from. Only a BRANDING sets these: it carries its own
   *  logo and banner as separate assets. A template ships no image, so its preview leaves them
   *  unset and draws the stock assets. */
  logoUrl?: string
  bannerUrl?: string
  allowDefer: boolean
  deferCount: number
  showCountdown: boolean
  countdownSeconds: number
  showProgress: boolean
  deploymentType: PreviewPhase
  /** False for `perform`/`skip`: no dialog is shown at all. */
  visible: boolean
  mode: UxDraft['mode']
  timeoutMinutes: number
  /** The app drawn in the dialog; the example app when unset. */
  sampleApp?: PreviewSampleApp
}

/** The facts the dialog shows, derived from the draft. Exported so a test can assert them
 *  without rendering — the derivation is the part that can be wrong. */
export function previewFacts(d: UxDraft, phase: PreviewPhase) {
  // ONE derivation for both deployment types, reading whichever side's fields apply. They carry
  // the same settings, so a preview that branched on the phase would be describing a difference
  // that no longer exists.
  const install = phase === 'install'
  const mode = install ? d.mode : d.uninstallMode
  const rawCountdown = install ? d.countdownSeconds : d.uninstallCountdownSeconds
  const allowDefer = install ? d.allowDefer : d.uninstallAllowDefer
  const deferCount = install ? d.deferCount : d.uninstallDeferCount
  const showProgress = install ? d.showProgress : d.uninstallShowProgress
  const force = install ? d.forceCountdown : d.uninstallForceCountdown
  const onTimeout = install ? d.onTimeout : d.uninstallOnTimeout
  // Each deployment type is its own PSADT run, so each has its own dialog timeout.
  const timeoutMinutes = install ? d.notifyTimeoutMinutes : d.uninstallNotifyTimeoutMinutes
  const timeoutSeconds = promptTimeoutSeconds(timeoutMinutes)

  // Does the welcome dialog show a live "Automatic Start Countdown" row at all? Ported from the
  // mock's showsCountdownRow. NOT "the field is non-zero": in `notify` + "postpone for them" the
  // config emits no countdown parameter whatsoever, so a row there advertised a timer the device
  // never starts.
  const showCountdown =
    mode === 'autoClose' ||
    (mode === 'notify' && onTimeout === 'proceed' && (force || !allowDefer))

  return {
    mode,
    visible: showsDialog(mode),
    // What the RUNTIME will count down. `notify` ignores the countdown field and counts the
    // dialog timeout instead (toConfig emits timeoutSeconds), so showing the field would be a
    // different number from the one the user watches.
    countdownSeconds:
      mode === 'notify' && onTimeout === 'proceed' ? timeoutSeconds : rawCountdown,
    showCountdown,
    // Deferral is only offered while the dialog is up; autoClose closes regardless.
    allowDefer: allowDefer && showsDialog(mode) && mode !== 'autoClose',
    deferCount,
    showProgress,
    timeoutMinutes,
  }
}

/**
 * Map the draft onto the preview config for one deployment type (the mock's `previewCfg`).
 * Identity is package-level, so both deployment types share it.
 */
export function previewCfg(d: UxDraft, side: PreviewPhase): PsadtPreviewConfig {
  const f = previewFacts(d, side)
  return {
    style: d.dialogStyle === 'modern' ? 'fluent' : 'classic',
    accent: normalizeAccent(d.fluentAccent),
    companyName: d.companyName,
    // A template ships no image, so its preview always draws the stock assets: the app icon in
    // the Fluent logo slot and Banner.Classic.png in the Classic banner slot. Branding images
    // are previewed where they are authored, never here.
    logoSource: 'appIcon',
    hasLogo: false,
    hasBanner: false,
    deploymentType: side,
    allowDefer: f.allowDefer,
    deferCount: f.deferCount,
    showCountdown: f.showCountdown,
    countdownSeconds: f.countdownSeconds,
    showProgress: f.visible && f.showProgress,
    visible: f.visible,
    mode: f.mode,
    timeoutMinutes: f.timeoutMinutes,
  }
}

/**
 * PSADT's Fluent dark theme renders the accent LIGHTER than in light mode (the Windows
 * "AccentLight" behaviour — same hue, higher lightness), because the light-mode accent would be
 * too dark on the charcoal card. Config exposes only one FluentAccentColor and the engine derives
 * the dark shade; approximate that by raising HSL lightness ~16 points.
 */
export function lightenForDark(hex: string): string {
  const m = hex.replace('#', '')
  if (m.length < 6) return hex
  const r = parseInt(m.slice(0, 2), 16) / 255
  const g = parseInt(m.slice(2, 4), 16) / 255
  const b = parseInt(m.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const delta = max - min
  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    if (max === r) h = ((((g - b) / delta) % 6) + 6) % 6
    else if (max === g) h = (b - r) / delta + 2
    else h = (r - g) / delta + 4
    h *= 60
  }
  const nl = Math.min(0.72, l + 0.16)
  const c2 = (1 - Math.abs(2 * nl - 1)) * s
  const x = c2 * (1 - Math.abs(((h / 60) % 2) - 1))
  const mm = nl - c2 / 2
  let rp = 0
  let gp = 0
  let bp = 0
  if (h < 60) { rp = c2; gp = x }
  else if (h < 120) { rp = x; gp = c2 }
  else if (h < 180) { gp = c2; bp = x }
  else if (h < 240) { gp = x; bp = c2 }
  else if (h < 300) { rp = x; bp = c2 }
  else { rp = c2; bp = x }
  const to = (v: number) => Math.round((v + mm) * 255).toString(16).padStart(2, '0')
  return `#${to(rp)}${to(gp)}${to(bp)}`
}

// ---------------------------------------------------------------- Fluent
type FCol = {
  bg: string
  panel: string
  border: string
  text: string
  sub: string
  btnSecBg: string
  btnSecBorder: string
  track: string
  a: string
}

/**
 * The mark near the top of the accent band.
 *
 * PSADT'S OWN ICON, not a drawing of one. This used to be a hand-inked shield SVG, which no part of
 * the toolkit renders: PSADT.UserInterface.dll exposes exactly three image slots — AppIconImage,
 * AppIconDarkImage and AppBannerImage — and none of them is a shield. A preview that invents
 * chrome teaches an author to expect something their devices will never show, which is the same
 * failure as previewing the wrong logo, only harder to notice because it looks deliberate.
 *
 * It is the toolkit's watermark rather than a user asset, so branding never replaces it: it stays
 * the PSADT mark, knocked out to white against the accent, whatever logo the template selects.
 */
const BandGlyph: React.FC = () => (
  <img
    src={PSADT_GLYPH}
    alt=""
    aria-hidden="true"
    width={22}
    height={22}
    className="absolute left-1/2 top-3 -translate-x-1/2"
    style={{ filter: 'brightness(0) invert(1)', opacity: 0.95, objectFit: 'contain' }}
    onError={(e) => {
      e.currentTarget.style.display = 'none'
    }}
  />
)

/** The app icon slot, or the app's initial when its icon is not known. */
const AppIcon: React.FC<{ src: string | null; title: string; size: number; className: string }> = ({
  src,
  title,
  size,
  className,
}) =>
  src ? (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className={className}
      style={{ objectFit: 'contain' }}
      onError={(e) => {
        e.currentTarget.style.visibility = 'hidden'
      }}
    />
  ) : (
    <span
      aria-hidden
      className={`${className} flex items-center justify-center bg-[#d0d0d0] font-semibold text-[#333]`}
      style={{ fontSize: Math.round(size / 2.2) }}
    >
      {title.trim().charAt(0).toUpperCase() || '?'}
    </span>
  )

const FluentHeader: React.FC<{ cfg: PsadtPreviewConfig; c: FCol }> = ({ cfg, c }) => {
  const s = stringsFor(cfg)
  const app = sampleOf(cfg)
  return (
    <div className="mb-3.5 flex items-center gap-3">
      {cfg.hasLogo && cfg.logoUrl ? (
        <img
          src={cfg.logoUrl}
          alt=""
          className="h-10 w-10 shrink-0 rounded object-contain"
          /* The slot is what the dialog reserves; if the image cannot be fetched the outline is
             still the truthful thing to show. */
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : (
        /* One square asset feeds both AppIconImage and AppIconDarkImage, so the same icon is
           what the device draws in either theme. */
        <AppIcon src={app.icon} title={app.title} size={40} className="h-10 w-10 shrink-0 rounded" />
      )}
      <div className="min-w-0">
        <p className="truncate text-[14px] font-semibold leading-tight">{app.title}</p>
        <p className="truncate text-[12px] leading-tight" style={{ color: c.sub }}>
          {(cfg.companyName.trim() || 'PSAppDeployToolkit') + s.fluent.subtitleSuffix}
        </p>
      </div>
    </div>
  )
}

const FluentWelcome: React.FC<{ cfg: PsadtPreviewConfig; c: FCol }> = ({ cfg, c }) => {
  const s = stringsFor(cfg)
  const app = sampleOf(cfg)
  return (
    <>
      <FluentHeader cfg={cfg} c={c} />
      <p className="mb-3 text-[12px] leading-relaxed">{s.fluent.dialogMessage}</p>

      <div
        className="mb-3 overflow-hidden rounded-md"
        style={{ background: c.panel, border: `1px solid ${c.border}` }}
      >
        {app.procs.map((p, i) => (
          <div
            key={`${p.exe ?? p.name}-${i}`}
            className="flex items-center gap-2.5 px-3 py-2"
            style={i > 0 ? { borderTop: `1px solid ${c.border}` } : undefined}
          >
            <AppIcon src={app.icon} title={app.title} size={24} className="h-6 w-6 shrink-0 rounded" />
            <div className="min-w-0 leading-tight">
              <p className="truncate text-[12px] font-semibold">{p.name}</p>
              {p.exe && (
                <p className="truncate text-[11px]" style={{ color: c.a }}>
                  {p.exe}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Only the countdown gets a row; the remaining-deferral count rides on the Defer button. */}
      {cfg.showCountdown && (
        <div className="mb-2 flex items-center justify-between py-[3px] text-[12px]">
          <span style={{ color: c.sub }}>{s.fluent.countdownLabel}</span>
          <span>{fmtHMS(cfg.countdownSeconds)}</span>
        </div>
      )}

      <div className="mt-4 flex gap-2.5">
        {cfg.allowDefer && (
          <span
            className="flex-1 rounded px-3 py-2 text-center text-[12px] font-semibold"
            style={{ background: c.btnSecBg, color: c.text, border: `1px solid ${c.btnSecBorder}` }}
          >
            {s.fluent.buttonRight}
            {cfg.deferCount ? ` (${cfg.deferCount} remain)` : ''}
          </span>
        )}
        <span
          className="flex-1 rounded px-3 py-2 text-center text-[12px] font-semibold text-white"
          style={{ background: c.a }}
        >
          {s.fluent.buttonLeft}
        </span>
      </div>
    </>
  )
}

const FluentProgress: React.FC<{ cfg: PsadtPreviewConfig; c: FCol }> = ({ cfg, c }) => {
  const s = stringsFor(cfg)
  return (
    <>
      <FluentHeader cfg={cfg} c={c} />
      <div
        className="overflow-hidden rounded-md"
        style={{ background: c.panel, border: `1px solid ${c.border}` }}
      >
        <div className="px-3.5 py-3">
          <p className="text-[13px] font-semibold leading-snug">{s.fluent.progressMsg}</p>
        </div>
        <div style={{ borderTop: `1px solid ${c.border}` }} />
        <div className="px-3.5 py-3">
          <p className="mb-2.5 text-[12px] leading-snug" style={{ color: c.sub }}>
            {s.fluent.progressDetail}
          </p>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: c.track }}
          >
            <div className="h-full rounded-full" style={{ width: '45%', background: c.a }} />
          </div>
        </div>
      </div>
    </>
  )
}

/** Borderless rounded card, ~450px wide, theme-aware, with a full-height left accent band and
 *  minimize-only chrome. */
const FluentDialog: React.FC<{
  cfg: PsadtPreviewConfig
  view: PsadtView
  theme: 'light' | 'dark'
}> = ({ cfg, view, theme }) => {
  const dark = theme === 'dark'
  const c: FCol = {
    bg: dark ? '#2b2b2b' : '#f9f9f9',
    panel: dark ? '#2f2f2f' : '#ffffff',
    border: dark ? '#3a3a3a' : '#e6e6e6',
    text: dark ? '#f3f3f3' : '#1a1a1a',
    sub: dark ? '#b8b8b8' : '#616161',
    btnSecBg: dark ? '#3a3a3a' : '#e8e8e8',
    btnSecBorder: dark ? '#4a4a4a' : '#d6d6d6',
    track: dark ? '#8a8a8a' : '#d0d0d0',
    a: dark ? lightenForDark(cfg.accent) : cfg.accent,
  }
  return (
    <div
      className="relative flex overflow-hidden"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        borderRadius: 8,
      }}
    >
      <div className="relative shrink-0" style={{ width: 40, background: c.a }}>
        {/* The toolkit's built-in watermark, not one of the three brandable slots. */}
        <BandGlyph />
      </div>
      <div className="relative min-w-0 flex-1">
        {/* minimize glyph only — the real dialog has no maximize and no close */}
        <span className="absolute right-3 top-2 text-[13px] leading-none" style={{ color: c.sub }}>
          ─
        </span>
        <div style={{ paddingLeft: 18, paddingRight: 18, paddingTop: 18, paddingBottom: 16 }}>
          {view === 'welcome'
            ? <FluentWelcome cfg={cfg} c={c} />
            : <FluentProgress cfg={cfg} c={c} />}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- Classic
const CL = {
  window: '#f0f0f0',
  titleBar: '#ffffff',
  text: '#000000',
  sub: '#1a1a1a',
  list: '#ffffff',
  listBorder: '#7a7a7a',
  btnBg: '#e1e1e1',
  btnBorder: '#adadad',
}

const ClassicBtn: React.FC<{ children: React.ReactNode; primary?: boolean }> = ({
  children,
  primary,
}) => (
  <span
    className="rounded-sm px-3 py-1 text-[11px]"
    style={{
      background: CL.btnBg,
      color: CL.text,
      border: `1px solid ${primary ? '#0078d7' : CL.btnBorder}`,
      boxShadow: primary ? '0 0 0 1px #0078d7 inset' : undefined,
    }}
  >
    {children}
  </span>
)

const ClassicDialog: React.FC<{ cfg: PsadtPreviewConfig; view: PsadtView }> = ({ cfg, view }) => {
  const s = stringsFor(cfg)
  const app = sampleOf(cfg)
  return (
    <div className="mx-auto" style={{ maxWidth: 450 }}>
      <div
        className="overflow-hidden"
        style={{ background: CL.window, border: '1px solid #6f6f6f', borderRadius: 3 }}
      >
        {/* Win32 title bar */}
        <div
          className="flex items-center justify-between px-2 py-1"
          style={{ background: CL.titleBar, borderBottom: '1px solid #dcdcdc' }}
        >
          <span className="text-[10px]" style={{ color: CL.text }}>
            {app.title}
          </span>
          <div className="flex items-center gap-2" style={{ color: '#888' }}>
            <span className="text-[11px] leading-none">─</span>
            <span className="text-[9px] leading-none">▢</span>
            <span className="text-[11px] leading-none">✕</span>
          </div>
        </div>

        {/* Assets\Banner.Classic.png. A branding preview passes its own banner; a template
            draws the banner our packages actually ship. */}
        {cfg.hasBanner && cfg.bannerUrl ? (
          <img
            src={cfg.bannerUrl}
            alt=""
            className="w-full object-contain"
            style={{ aspectRatio: '450 / 100', borderBottom: '1px solid #dcdcdc',
                     background: '#ffffff' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <img
            src={DEFAULT_BANNER}
            alt=""
            className="block w-full"
            style={{
              // The shipped asset's real proportions. Rendering it at 900/125 letterboxed it, so
              // the preview drew the banner narrower than the device does.
              aspectRatio: '450 / 100',
              objectFit: 'contain',
              background: '#ffffff',
              borderBottom: '1px solid #dcdcdc',
            }}
          />
        )}

        <div className="px-3.5 py-3" style={{ color: CL.text }}>
          {view === 'welcome' ? (
            <>
              <p
                className="mb-2.5 whitespace-pre-line text-[11px] leading-relaxed"
                style={{ color: CL.sub }}
              >
                {s.classic.closeAppsMsg}
              </p>
              <div
                className="mb-2.5 rounded-sm px-2 py-1"
                style={{ background: CL.list, border: `1px solid ${CL.listBorder}` }}
              >
                {app.procs.map((p, i) => (
                  <p
                    key={`${p.exe ?? p.name}-${i}`}
                    className="py-0.5 text-[11px]"
                    style={i > 0 ? { borderTop: '1px solid #eee' } : undefined}
                  >
                    {p.name}
                  </p>
                ))}
              </div>
              {cfg.allowDefer && (
                <p className="mb-1 text-[11px]" style={{ color: CL.sub }}>
                  {s.classic.deferralsLabel}{' '}
                  <span className="font-semibold">{cfg.deferCount}</span>
                </p>
              )}
              {cfg.showCountdown && (
                <p className="mb-2.5 text-[11px]" style={{ color: CL.sub }}>
                  {s.classic.countdownDefer}{' '}
                  <span className="font-mono font-semibold">
                    {fmtCountdown(cfg.countdownSeconds)}
                  </span>
                </p>
              )}
              <div className="mt-2.5 flex justify-center gap-2">
                <ClassicBtn primary>{s.classic.buttonClose}</ClassicBtn>
                {cfg.allowDefer && <ClassicBtn>{s.classic.buttonDefer}</ClassicBtn>}
                <ClassicBtn>{s.classic.buttonContinue}</ClassicBtn>
              </div>
            </>
          ) : (
            <>
              <p className="mb-2.5 text-[11px]" style={{ color: CL.sub }}>
                {s.classic.progressMsg}
              </p>
              <div
                className="h-3.5 w-full overflow-hidden rounded-sm"
                style={{ background: CL.list, border: `1px solid ${CL.listBorder}` }}
              >
                <div
                  className="h-full w-1/3 animate-[psadtMarquee_1.4s_linear_infinite]"
                  style={{ background: '#0078d7' }}
                />
              </div>
              <style>{'@keyframes psadtMarquee{0%{margin-left:-33%}100%{margin-left:100%}}'}</style>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- wrapper
export interface PsadtDialogPreviewProps {
  draft: UxDraft
  phase?: PreviewPhase
  /** The app the template is being applied to. Without one the preview draws an example app. */
  sampleApp?: PreviewSampleApp
}

const SwitchBtn: React.FC<{
  on: boolean
  onClick: () => void
  title?: string
  children: React.ReactNode
}> = ({ on, onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`px-2 py-1 text-[11px] font-medium ${
      on
        ? 'bg-theme-tertiary text-theme-text-primary'
        : 'text-theme-text-tertiary hover:text-theme-text-secondary'
    }`}
  >
    {children}
  </button>
)

/**
 * The dialog itself, with its style/theme/view switches, driven by a config rather than a draft.
 *
 * Split out so BRANDING previews through the very same renderer a template does. Branding decides
 * the logo, banner, company name, dialog style and accent — the same slots this draws — and a second
 * implementation would drift from this one silently, leaving one of the two screens promising an
 * appearance the device never shows.
 */
/** The width a PSADT dialog is drawn at. The dialog is a fixed-size window on the device, so a
 *  preview stretched to its container stops being a preview of anything — the proportions, and
 *  therefore how the logo and banner actually sit, are the whole point of looking at it.
 *
 *  Exported because two screens draw this surface: the user-experience wizard through
 *  PsadtDialogPreview, and the branding wizard directly. The branding wizard used the surface
 *  WITHOUT this bound and stretched across the full-width page. */
export const PSADT_PREVIEW_MAX_WIDTH = 500

export const PsadtDialogSurface: React.FC<{ cfg: PsadtPreviewConfig }> = ({ cfg }) => {
  const views: PsadtView[] = cfg.showProgress ? ['welcome', 'progress'] : ['welcome']
  const [view, setView] = useState<PsadtView>('welcome')
  // Fluent is the only engine with a theme; PSADT derives the dark accent itself.
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const activeView = views.includes(view) ? view : 'welcome'

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        {views.length > 1 ? (
          <div className="flex overflow-hidden rounded-md border border-theme-border-primary">
            {views.map((v) => (
              <SwitchBtn key={v} on={activeView === v} onClick={() => setView(v)}>
                {v === 'welcome' ? 'Welcome' : 'Progress'}
              </SwitchBtn>
            ))}
          </div>
        ) : (
          <span />
        )}
        {cfg.style === 'fluent' ? (
          <div className="flex overflow-hidden rounded-md border border-theme-border-primary">
            {(['light', 'dark'] as const).map((t) => (
              <SwitchBtn
                key={t}
                on={theme === t}
                onClick={() => setTheme(t)}
                title={`${t} theme`}
              >
                {t === 'light' ? 'Light' : 'Dark'}
              </SwitchBtn>
            ))}
          </div>
        ) : (
          <span className="text-[10px] text-theme-text-tertiary">Classic has no theming</span>
        )}
      </div>

      {cfg.style === 'fluent' ? (
        <FluentDialog cfg={cfg} view={activeView} theme={theme} />
      ) : (
        <ClassicDialog cfg={cfg} view={activeView} />
      )}
    </>
  )
}

const PsadtDialogPreview: React.FC<PsadtDialogPreviewProps> = ({
  draft,
  phase = 'install',
  sampleApp,
}) => {
  const cfg = { ...previewCfg(draft, phase), sampleApp }

  if (!cfg.visible) {
    // Silent and skip show nothing at all. Saying so beats rendering a dialog the user will
    // never see next to a template configured not to show one.
    return (
      <div className="rounded-lg border border-dashed border-theme-border-primary p-4 text-xs text-theme-text-tertiary">
        {cfg.mode === 'perform'
          ? `No dialog. Apps are closed and the ${
              phase === 'install' ? 'install' : 'removal'
            } proceeds silently.`
          : `No dialog. The ${
              phase === 'install' ? 'install' : 'removal'
            } is skipped while a target app is in use and Intune retries later.`}
      </div>
    )
  }

  return (
    <div className="mx-auto w-full" style={{ maxWidth: PSADT_PREVIEW_MAX_WIDTH }}>
      <PsadtDialogSurface cfg={cfg} />

      <p className="mt-3 text-center text-xs text-theme-text-tertiary">
        {cfg.style === 'fluent' ? 'Fluent' : 'Classic'} dialog,{' '}
        {phase === 'install' ? 'install' : 'uninstall'}.{' '}
        {/* The countdown mode has no authored wait: the countdown is what ends the dialog, and
            the renderer sizes the dialog timeout to fit it. Naming the stored wait here reported a
            number the device may never reach. */}
        {cfg.mode === 'autoClose'
          ? 'Apps are closed when the countdown ends.'
          : `Closes after ${cfg.timeoutMinutes} min if nobody answers.`}
        {cfg.showProgress ? ' A progress dialog follows.' : ' No progress dialog follows.'}
      </p>
      {!sampleApp && (
        <p className="mt-1 text-center text-[11px] text-theme-text-tertiary">
          The app and its processes are a stand-in — a template is not bound to one app.
        </p>
      )}
    </div>
  )
}

export default PsadtDialogPreview
